// src/pages/EventForm.js
//
// Formulaire de CRÉATION d'un événement.
// Pour l'édition d'un événement existant, voir EventEdit.js (même structure visuelle).
//
// NOUVEAU (billetterie workshops / stages) :
//  • Section « Publication sur le site » (components/events/PublicEventSection) :
//    visibilité, image, Instagram, lieu, âges, date limite, tickets et prix.
//  • L'identifiant de l'événement est réservé dès l'ouverture du formulaire
//    (doc(collection(...))) pour pouvoir ranger l'image dans
//    event-images/{eventId}/ avant le premier enregistrement.
//  • Dates construites en heure locale (combineLocalDateTime) au lieu de
//    new Date('YYYY-MM-DD'), interprété en UTC par le navigateur.
//
// NOUVEAU (plusieurs cours) :
//  • Quand l'événement n'est pas ouvert à tous, on peut cibler PLUSIEURS cours.
//  • Stockage : `selectedCourses` (tableau d'IDs) = champ de référence.
//    `selectedCourse` (premier ID) est conservé pour la rétrocompatibilité
//    avec les écrans qui lisent encore l'ancien champ unique.

import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, Grid, Typography, MenuItem,
  FormControl, InputLabel, Select, FormControlLabel, Checkbox,
  Alert, Paper, InputAdornment, Snackbar, CircularProgress,
  Switch, Stack, Chip, ListItemText, FormHelperText,
} from '@mui/material';
import {
  EventNote as EventNoteIcon,
  Info as InfoIcon,
  Group as GroupIcon,
  CalendarMonth as CalendarMonthIcon,
  Euro as EuroIcon,
  Category as CategoryIcon,
  Notes as NotesIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { db } from '../firebase';
import { collection, doc, setDoc, Timestamp, getDocs, query } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import PublicEventSection from '../components/events/PublicEventSection';
import {
  defaultPublicForm, validatePublicForm, buildPublicPayload, isSlugTaken,
  combineLocalDateTime, suggestSlug,
} from '../utils/publicEvents';

const DESCRIPTION_MAX_LENGTH = 3000;

// ─── En-tête de section réutilisable ──────────────────────────────────────
function SectionHeader({ icon, title, subtitle }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 36, height: 36, borderRadius: '10px',
          backgroundColor: '#EEF2FF', color: '#4338CA', flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default function EventForm() {
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: '',
    newCategory: '',
    isForAllStudents: true,
    selectedCourses: [],
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    allDay: false,
    price: 0,
  });

  // Section billetterie / site
  const [publicForm, setPublicForm] = useState(defaultPublicForm);
  // Identifiant réservé dès l'ouverture (chemin de l'image dans Storage)
  const [eventRef] = useState(() => doc(collection(db, 'events')));

  const [categories, setCategories] = useState([]);
  const [courses, setCourses] = useState([]);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Récupérer les catégories existantes depuis les événements
        const eventsQuery = query(collection(db, 'events'));
        const eventsSnapshot = await getDocs(eventsQuery);

        const categoriesSet = new Set();
        eventsSnapshot.forEach(d => {
          const eventData = d.data();
          if (eventData.category) categoriesSet.add(eventData.category);
        });
        setCategories(Array.from(categoriesSet).sort());

        // Récupérer les cours (triés par titre pour faciliter la sélection)
        const coursesQuery = query(collection(db, 'dance_courses'));
        const coursesSnapshot = await getDocs(coursesQuery);
        setCourses(
          coursesSnapshot.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (a.title || '').localeCompare(b.title || '', 'fr'))
        );

        setLoading(false);
      } catch (err) {
        console.error("Erreur de chargement :", err);
        setError("Erreur lors du chargement des données");
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handlePublicChange = (patch) => {
    setPublicForm(prev => ({ ...prev, ...patch }));
  };

  // Sélection multiple des cours (MUI peut renvoyer une chaîne avec l'autofill)
  const handleCoursesChange = (e) => {
    const value = e.target.value;
    handleChange('selectedCourses', typeof value === 'string' ? value.split(',') : value);
  };

  const courseTitleById = (id) => courses.find(c => c.id === id)?.title || id;

  // Tant que l'adresse de la page n'a pas été modifiée à la main, elle suit
  // le titre et la date.
  useEffect(() => {
    setPublicForm(prev => (
      prev.isPublic && !prev.slugTouched
        ? { ...prev, slug: suggestSlug(form.title, form.startDate) }
        : prev
    ));
  }, [form.title, form.startDate]);

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    if (value === 'add_new') {
      setShowNewCategory(true);
      handleChange('category', '');
    } else {
      setShowNewCategory(false);
      handleChange('category', value);
    }
  };

  const handleSubmit = async () => {
    setError(null);
    try {
      // Validation des champs obligatoires
      if (!form.title.trim()) throw new Error("Le titre est obligatoire");
      if (!form.category && !form.newCategory) throw new Error("La catégorie est obligatoire");
      if (!form.isForAllStudents && form.selectedCourses.length === 0) {
        throw new Error("Sélectionnez au moins un cours, ou ouvrez l'événement à tous les élèves");
      }
      if (!form.startDate || !form.endDate) throw new Error("Les dates sont obligatoires");
      if (!form.allDay && (!form.startTime || !form.endTime)) throw new Error("Les heures sont obligatoires");

      // Combinaison date + heure, en heure locale
      const startDateTime = combineLocalDateTime(form.startDate, form.allDay ? '00:00' : form.startTime);
      const endDateTime = combineLocalDateTime(form.endDate, form.allDay ? '00:00' : form.endTime);
      if (!startDateTime || !endDateTime) throw new Error("Dates invalides");
      if (form.allDay) endDateTime.setHours(23, 59, 59, 999);

      if (endDateTime < startDateTime) {
        throw new Error("La date/heure de fin doit être après la date/heure de début");
      }

      validatePublicForm(publicForm, {
        title: form.title,
        endDateTime,
        ticketStock: {},
      });

      setSaving(true);

      if (publicForm.isPublic && await isSlugTaken(db, publicForm.slug, eventRef.id)) {
        throw new Error(`L'adresse /evenements/${publicForm.slug} est déjà utilisée par un autre événement.`);
      }

      // Calcul de la durée
      const durationMs = endDateTime - startDateTime;
      const durationHours = Math.floor(durationMs / (1000 * 60 * 60));
      const durationMinutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
      const durationStr = `${durationHours}h${durationMinutes.toString().padStart(2, '0')}`;

      const title = form.title.trim();
      const description = (form.description || '').trim();

      const eventData = {
        title,
        description,
        category: showNewCategory ? form.newCategory.trim() : form.category,
        isForAllStudents: form.isForAllStudents,
        startDate: Timestamp.fromDate(startDateTime),
        endDate: Timestamp.fromDate(endDateTime),
        allDay: form.allDay,
        price: Number(form.price) || 0,
        participantsCount: 0,
        selectedStudents: [],
        duration: durationStr,
        createdAt: Timestamp.now(),
        startTime: form.allDay ? null : form.startTime,
        endTime: form.allDay ? null : form.endTime,
        // Billetterie (écrase "price" par le prix d'appel des tickets si public)
        ...buildPublicPayload(publicForm, { title, description }),
      };

      if (!form.isForAllStudents && form.selectedCourses.length > 0) {
        eventData.selectedCourses = form.selectedCourses;
        // Rétrocompatibilité : ancien champ unique lu par les autres écrans
        eventData.selectedCourse = form.selectedCourses[0];
      }

      await setDoc(eventRef, eventData);

      setSnackbar({ open: true, message: 'Événement créé avec succès', severity: 'success' });
      setTimeout(() => navigate(publicForm.isPublic ? `/events/${eventRef.id}` : '/events'), 600);
    } catch (error) {
      console.error("Erreur lors de l'ajout :", error);
      setError(error.message);
      setSaving(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 2 }}>
        <CircularProgress />
        <Typography color="text.secondary">Chargement du formulaire...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh', p: 3 }}>
      <Box sx={{ maxWidth: 960, margin: '0 auto' }}>

        {/* ── En-tête ─────────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/events')}
            sx={{ borderRadius: 2 }}
          >
            Retour
          </Button>
          <Box sx={{ flexGrow: 1 }} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 52, height: 52, borderRadius: '14px',
              backgroundColor: '#4caf50', color: 'white', boxShadow: 2,
            }}
          >
            <EventNoteIcon fontSize="medium" />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight="bold" color="#2c3e50">
              Ajouter un événement
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Événement interne, ou workshop / stage publié sur le site avec billetterie
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Stack spacing={3}>
          {/* ── Informations générales ──────────────────────────────── */}
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <SectionHeader
              icon={<InfoIcon />}
              title="Informations générales"
              subtitle="Titre, catégorie, description et prix"
            />
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Titre de l'événement"
                  required
                  variant="outlined"
                  value={form.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="Ex : Workshop Hip Hop avec Jay"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={12}
                  label="Description"
                  variant="outlined"
                  value={form.description}
                  onChange={(e) => {
                    if (e.target.value.length <= DESCRIPTION_MAX_LENGTH) {
                      handleChange('description', e.target.value);
                    }
                  }}
                  placeholder="Programme, intervenant·e, informations utiles pour les participants..."
                  helperText={`${form.description.length} / ${DESCRIPTION_MAX_LENGTH} caractères`}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                        <NotesIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Catégorie *</InputLabel>
                  <Select
                    value={showNewCategory ? 'add_new' : form.category}
                    onChange={handleCategoryChange}
                    label="Catégorie *"
                    startAdornment={
                      <InputAdornment position="start">
                        <CategoryIcon color="action" fontSize="small" sx={{ ml: 0.5 }} />
                      </InputAdornment>
                    }
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                    ))}
                    <MenuItem value="add_new" sx={{ fontStyle: 'italic', color: 'primary.main' }}>
                      + Ajouter une nouvelle catégorie
                    </MenuItem>
                  </Select>
                </FormControl>
                {showNewCategory && (
                  <TextField
                    fullWidth
                    label="Nouvelle catégorie"
                    required
                    variant="outlined"
                    value={form.newCategory}
                    onChange={(e) => handleChange('newCategory', e.target.value)}
                    sx={{ mt: 2 }}
                    autoFocus
                  />
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                {publicForm.isPublic ? (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Les prix sont définis par ticket dans « Publication sur le site ».
                  </Alert>
                ) : (
                  <TextField
                    fullWidth
                    type="number"
                    label="Prix"
                    variant="outlined"
                    value={form.price}
                    onChange={(e) => handleChange('price', e.target.value)}
                    InputProps={{
                      inputProps: { min: 0, step: 0.5 },
                      startAdornment: (
                        <InputAdornment position="start">
                          <EuroIcon color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Laissez à 0 pour un événement gratuit"
                  />
                )}
              </Grid>
            </Grid>
          </Paper>

          {/* ── Public concerné ─────────────────────────────────────── */}
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <SectionHeader
              icon={<GroupIcon />}
              title="Public concerné"
              subtitle="Qui peut participer à cet événement ?"
            />

            <Box
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                p: 2, borderRadius: 2, backgroundColor: '#f8fafc', mb: form.isForAllStudents ? 0 : 2,
              }}
            >
              <Box>
                <Typography fontWeight={600}>Ouvert à tous les élèves</Typography>
                <Typography variant="caption" color="text.secondary">
                  Désactivez pour cibler un ou plusieurs cours
                </Typography>
              </Box>
              <Switch
                checked={form.isForAllStudents}
                onChange={(e) => handleChange('isForAllStudents', e.target.checked)}
              />
            </Box>

            {!form.isForAllStudents && (
              <FormControl fullWidth variant="outlined" sx={{ mt: 2 }}>
                <InputLabel>Cours concernés *</InputLabel>
                <Select
                  multiple
                  value={form.selectedCourses}
                  onChange={handleCoursesChange}
                  label="Cours concernés *"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((id) => (
                        <Chip
                          key={id}
                          label={courseTitleById(id)}
                          size="small"
                          onMouseDown={(e) => e.stopPropagation()}
                          onDelete={() => handleChange(
                            'selectedCourses',
                            form.selectedCourses.filter(c => c !== id),
                          )}
                        />
                      ))}
                    </Box>
                  )}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
                >
                  {courses.map((course) => (
                    <MenuItem key={course.id} value={course.id}>
                      <Checkbox
                        size="small"
                        checked={form.selectedCourses.includes(course.id)}
                      />
                      <ListItemText primary={course.title} />
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {form.selectedCourses.length === 0
                    ? 'Sélectionnez un ou plusieurs cours'
                    : `${form.selectedCourses.length} cours sélectionné${form.selectedCourses.length > 1 ? 's' : ''}`}
                </FormHelperText>
              </FormControl>
            )}
          </Paper>

          {/* ── Dates et horaires ───────────────────────────────────── */}
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <SectionHeader
              icon={<CalendarMonthIcon />}
              title="Dates et horaires"
              subtitle="Quand se déroule l'événement ?"
            />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date de début"
                  required
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  value={form.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date de fin"
                  required
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  value={form.endDate}
                  onChange={(e) => handleChange('endDate', e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.allDay}
                      onChange={(e) => handleChange('allDay', e.target.checked)}
                    />
                  }
                  label="Toute la journée"
                />
              </Grid>

              {!form.allDay && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="time"
                      label="Heure de début"
                      required
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      value={form.startTime}
                      onChange={(e) => handleChange('startTime', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="time"
                      label="Heure de fin"
                      required
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      value={form.endTime}
                      onChange={(e) => handleChange('endTime', e.target.value)}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>

          {/* ── Publication sur le site / billetterie ───────────────── */}
          <PublicEventSection
            value={publicForm}
            onChange={handlePublicChange}
            eventId={eventRef.id}
            ticketStock={{}}
            title={form.title}
            startDate={form.startDate}
            disabled={saving}
          />
        </Stack>

        {/* ── Barre d'actions ─────────────────────────────────────────── */}
        <Box
          sx={{
            display: 'flex', justifyContent: 'flex-end', gap: 2,
            mt: 3, mb: 2, position: 'sticky', bottom: 16,
          }}
        >
          <Paper
            elevation={4}
            sx={{
              display: 'flex', gap: 2, p: 1.5, borderRadius: 3,
              backgroundColor: 'white',
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate('/events')}
              disabled={saving}
              sx={{ borderRadius: 2, px: 3 }}
            >
              Annuler
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              sx={{
                backgroundColor: '#4caf50',
                '&:hover': { backgroundColor: '#388e3c' },
                fontWeight: 'bold',
                borderRadius: 2,
                px: 3,
              }}
            >
              {saving
                ? 'Enregistrement...'
                : publicForm.isPublic && publicForm.status === 'published'
                  ? 'Enregistrer et mettre en ligne'
                  : "Enregistrer l'événement"}
            </Button>
          </Paper>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}